<template>
  <div class="col-12 col-xxl-12 ">
    <div class="card mb-3 mb-xxl-5 me-xxl-4" style="margin-top: 4rem; overflow: visible">
      <div class="card-header p-0 position-relative mt-n4 mx-3 z-index-2" style="height: 60px">
        <div class="bg-gradient-primary shadow-primary border-radius-lg pt-4 pb-3 h-100">
          <h6 class="text-white text-capitalize ps-3 pe-3"  style="font-size: 0.9rem; font-size: 1rem">
            {{ $t('teachersTable.title') }}
          </h6>
        </div>
      </div>

      <div class="card-body px-0 pb-0 pb-2 pe-xl-3">
        <div class="table-responsive p-1 p-xl-4">
          <LoadingSpinner
            v-if="loading && teachers.length === 0"
            :message="$t('teachersTable.loading')"
            class="py-5"
          />

          <template v-else>
            <div class="d-block d-xxl-none">
              <div v-if="!loading && teachers.length === 0" class="text-center py-3 text-muted">
                <i class="fas fa-chalkboard-teacher fa-2x mb-2"></i>
                <p>{{ $t('teachersTable.noTeachers') }}</p>
              </div>

              <div v-for="teacher in teachers" :key="'mobile-' + teacher.id" class="card mb-3 mx-2">
                <div class="card-body py-2">
                  <div class="d-flex align-items-center">
                    <div class="avatar-container me-2">
                      <img
                        :src="teacher.image_url || '/images/default-avatar.png'"
                        class="user-avatar rounded-circle"
                        style="width: 40px; height: 40px"
                        :alt="$t('teachersTable.defaultAvatarAlt')"
                        @error="handleImageError"
                      />
                    </div>
                    <div class="flex-grow-1">
                      <h6 class="mb-0 text-sm">{{ teacher.username }}</h6>
                      <p class="text-xs text-muted mb-0">{{ teacher.email }}</p>
                    </div>
                    <div>
                      <span class="badge badge-sm" :class="teacher.gender === 'M' ? 'bg-primary' : 'bg-secondary'">
                        {{ teacher.gender === 'M' ? $t('teachersTable.male') : $t('teachersTable.female') }}
                      </span>
                    </div>
                  </div>
                  
                  <div class="mt-2">
                    <p class="text-xs font-weight-bold mb-1">
                      {{ teacher.qualification }}
                      <span v-if="teacher.specialization" class="text-muted"> — {{ teacher.specialization }}</span>
                    </p>
                    <p class="text-xs text-muted mb-1">
                      {{ teacher.salary_type === 'per_lesson' ? '—' : (teacher.salary + ' د.ل') }}
                    </p>
                    <div class="d-flex flex-wrap align-items-center gap-2">
                      <span
                        v-if="teacher.salary_type"
                        class="badge badge-sm"
                        :class="salaryTypeBadgeClass(teacher.salary_type)"
                      >
                        {{ salaryTypeLabel(teacher.salary_type) }}
                      </span>
                      <span
                        class="badge badge-sm"
                        :class="teacher.is_active ? 'bg-success' : 'bg-danger'"
                      >
                        {{ teacher.is_active ? $t('teachersTable.active') : $t('teachersTable.inactive') }}
                      </span>
                    </div>
                  </div>
                  
                  <div class="d-flex justify-content-end mt-2">
                    <router-link
                      :to="{ name: 'teachers.edit', params: { id: teacher.id } }"
                      class="btn btn-link text-secondary p-0 mx-1"
                      :title="$t('teachersTable.edit')"
                      v-can="'teachers.update'"
                    >
                      <i class="fas fa-edit fa-xs"></i>
                    </router-link>
                    <button
                      @click="openDeleteDialog(teacher)"
                      class="btn btn-link text-danger p-0 mx-1"
                      :title="$t('teachersTable.delete')"
                      :disabled="isProcessing"
                      v-can="'teachers.delete'"
                    >
                      <i class="fas fa-trash fa-xs"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <table class="table align-items-center mb-0 d-none d-xxl-table">
              <thead>
                <tr>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teachersTable.image') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teachersTable.name') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7">{{ $t('teachersTable.email') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teachersTable.gender') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teachersTable.qualification') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teachersTable.specialization') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teachersTable.salary') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teachersTable.salaryType') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center">{{ $t('teachersTable.status') }}</th>
                  <th class="text-uppercase text-xxs font-weight-bolder opacity-7 text-center pe-4">{{ $t('teachersTable.actions') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!loading && teachers.length === 0">
                  <td colspan="10" class="text-center py-4 text-muted">
                    <i class="fas fa-chalkboard-teacher fa-2x mb-2"></i>
                    <p>{{ $t('teachersTable.noTeachers') }}</p>
                  </td>
                </tr>
                
                <template v-if="!loading">
                  <tr v-for="teacher in teachers" :key="teacher.id">
                    <td>
                      <div class="avatar-container">
                        <img
                          :src="teacher.image_url || '/images/default-avatar.png'"
                          class="user-avatar rounded-circle"
                          :alt="$t('teachersTable.defaultAvatarAlt')"
                          @error="handleImageError"
                        />
                      </div>
                    </td>
                    <td>
                      <div class="d-flex px-3 py-1">
                        <div class="d-flex flex-column justify-content-center">
                          <h6 class="mb-0 text-md">{{ teacher.username }}</h6>
                        </div>
                      </div>
                    </td>
                    <td>
                      <p class="text-xs font-weight-bold mb-0">{{ teacher.email }}</p>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="teacher.gender === 'M' ? 'bg-primary' : 'bg-secondary'"
                      >
                        {{ teacher.gender === 'M' ? $t('teachersTable.male') : $t('teachersTable.female') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">
                        {{ teacher.qualification }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">
                        {{ teacher.specialization || '—' }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span class="text-xs font-weight-bold">
                        {{ teacher.salary_type === 'per_lesson' ? '—' : (teacher.salary + ' د.ل') }}
                      </span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        v-if="teacher.salary_type"
                        class="badge badge-sm"
                        :class="salaryTypeBadgeClass(teacher.salary_type)"
                      >
                        {{ salaryTypeLabel(teacher.salary_type) }}
                      </span>
                      <span v-else class="text-xs text-muted">—</span>
                    </td>
                    <td class="align-middle text-center">
                      <span
                        class="badge badge-sm"
                        :class="teacher.is_active ? 'bg-success' : 'bg-danger'"
                      >
                        {{ teacher.is_active ? $t('teachersTable.active') : $t('teachersTable.inactive') }}
                      </span>
                    </td>
                    <td class="align-middle text-end pe-4">
                      <div class="d-inline-flex">
                        <router-link
                          :to="{ name: 'teachers.edit', params: { id: teacher.id } }"
                          class="btn btn-link text-secondary p-1 mx-1"
                          :title="$t('teachersTable.edit')"
                          v-can="'teachers.update'"
                        >
                          <i class="fas fa-edit fa-sm"></i>
                        </router-link>
                        <button
                          @click="openDeleteDialog(teacher)"
                          class="btn btn-link text-danger p-1 mx-1"
                          :title="$t('teachersTable.delete')"
                          :disabled="isProcessing"
                          v-can="'teachers.delete'"
                        >
                          <i class="fas fa-trash fa-sm"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits } from "vue";
import { useI18n } from "vue-i18n";
import LoadingSpinner from "@/components/common/LoadingSpinner.vue";

const { t } = useI18n();

defineProps({
  teachers: {
    type: Array,
    required: true,
    default: () => [],
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

const isProcessing = ref(false);
const emit = defineEmits(["delete-teacher"]);

const openDeleteDialog = (teacher) => {
  emit("delete-teacher", teacher);
};

const handleImageError = (event) => {
  event.target.src = '/images/default-avatar.png';
};

// Fixed-salary-only teachers have no per-lesson rate, and per-lesson-only
// teachers have no fixed salary — showing "0 د.ل" for the inapplicable one
// reads as a real (wrong) amount, so this label tells the admin how the
// teacher is actually paid instead.
const salaryTypeLabel = (type) => {
  if (type === 'fixed') return t('teachers.salaryTypeFixed');
  if (type === 'per_lesson') return t('teachers.salaryTypePerLesson');
  if (type === 'fixed_plus_lessons') return t('teachers.salaryTypeFixedPlusLessons');
  return '—';
};

const salaryTypeBadgeClass = (type) => {
  if (type === 'fixed') return 'bg-info';
  if (type === 'per_lesson') return 'bg-warning';
  if (type === 'fixed_plus_lessons') return 'bg-primary';
  return 'bg-secondary';
};
</script>
